import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';

export interface StripeSettings {
  project_id: string;
  stripe_mode: 'test' | 'live';
  stripe_connect_account_id: string | null;
  stripe_connect_account_id_test: string | null;
  stripe_connect_account_id_live: string | null;
  client_stripe_customer_id: string | null;
  created_at: number | null;
  updated_at: number | null;
}

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) {
    return source.error;
  }

  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }

  if (typeof source.message === 'string' && source.message) {
    return source.message;
  }

  if (source.data && typeof source.data.message === 'string' && source.data.message) {
    return source.data.message;
  }

  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestStripeSettingsApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();

  if (!token) {
    throw new Error('You need to sign in again before changing Stripe setup.');
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The Stripe setup workspace could not reach the Bliss app API.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function getStripeSettings(): Promise<StripeSettings> {
  return requestStripeSettingsApi<StripeSettings>('/settings/stripe');
}

export async function updateStripeSettings(input: {
  stripe_mode: 'test' | 'live';
  stripe_connect_account_id: string | null;
}): Promise<StripeSettings> {
  return requestStripeSettingsApi<StripeSettings>('/settings/stripe', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function startStripeConnectOnboarding(input: {
  return_url: string;
  refresh_url: string;
  email?: string;
  mode: 'test' | 'live';
}): Promise<{ onboarding_url: string; account_id: string }> {
  return requestStripeSettingsApi<{ onboarding_url: string; account_id: string }>(
    '/settings/stripe-connect/onboard',
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
}
